import { numberParam } from '../types/checks.js';
import { clamp } from '../types/geometry.js';
import type { CheckRunContext } from '../types/checks.js';
import type { MeasurementStatus } from '../types/common.js';
import type { AuthenticityAssessment, AuthenticitySignal, AuthenticitySignalId } from './index.js';
import { cloneSignal, compressionHistorySignal, resamplingSignal } from './resampling.js';
import { faceTextureSignal, skinSmoothingSignal } from './skinTexture.js';
import { boundarySignal, noiseMismatchSignal } from './backgroundReplacement.js';

/**
 * The combined authenticity risk.
 *
 * Every signal keeps its own score and confidence; the risk is the weighted
 * mean of the signals that could actually run, and the assessment confidence is
 * additionally scaled by how much of the requested evidence was available. A
 * single signal never decides alone, and `metadata` cannot push the risk across
 * the warning threshold without support from a pixel-level signal.
 */

export const AUTHENTICITY_WEIGHTS: Record<AuthenticitySignalId, number> = {
  compression_history: 0.15,
  metadata: 0.05,
  resampling: 0.12,
  face_texture: 0.16,
  skin_smoothing: 0.14,
  boundary: 0.12,
  noise_mismatch: 0.14,
  clone: 0.18,
};

export const SIGNAL_ORDER: readonly AuthenticitySignalId[] = [
  'compression_history',
  'metadata',
  'resampling',
  'face_texture',
  'skin_smoothing',
  'boundary',
  'noise_mismatch',
  'clone',
];

/** Signals below this confidence are treated as "could not run". */
const UNAVAILABLE_CONFIDENCE = 0.05;

/**
 * Signal B — metadata.
 *
 * An editing `Software` tag is a strong hint about the toolchain, not about the
 * person, and EXIF is stripped by nearly every messenger — hence the very low
 * weight and the support rule in `assessAuthenticity`.
 */
const EDITOR_PATTERNS = [
  'photoshop',
  'gimp',
  'lightroom',
  'snapseed',
  'faceapp',
  'remini',
  'affinity',
  'capture one',
  'pixelmator',
  'paint.net',
  'luminar',
  'portraitpro',
  'facetune',
  'meitu',
  'picsart',
  'canva',
  'krita',
  'darktable',
  'rawtherapee',
  'topaz',
  'acdsee',
  'corel',
];

export const metadataSignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const metadata = ctx.fileInfo.metadata;
  if (!metadata) {
    return {
      id: 'metadata',
      score: 0,
      confidence: 0.02,
      note: 'Метаданные файла не были разобраны.',
    };
  }

  const exif = metadata.exif;
  const software = exif?.software?.trim();
  const softwareLower = software?.toLowerCase() ?? '';
  const editorHit = EDITOR_PATTERNS.find((pattern) => softwareLower.includes(pattern));

  const hasExif = exif !== undefined && Object.keys(exif).length > 0;
  const hasDate = typeof exif?.dateTimeOriginal === 'string' && exif.dateTimeOriginal.length > 0;
  const hasCamera = Boolean(exif?.make || exif?.model);
  const iccDescription = exif?.iccProfile?.description?.toLowerCase() ?? '';
  const editorIcc = ['adobe', 'prophoto', 'lightroom', 'photoshop'].find((pattern) =>
    iccDescription.includes(pattern),
  );
  const mime = (ctx.fileInfo.detectedMimeType ?? ctx.image.mimeType ?? '').toLowerCase();
  const jpegLike = mime.includes('jpeg') || mime.includes('jpg');

  const reasons: string[] = [];
  let score = 0;

  if (editorHit) {
    reasons.push(`в метаданных указан редактор: ${software}`);
    score = Math.max(score, 0.7);
  } else if (editorIcc) {
    reasons.push('цветовой профиль похож на профиль редактора');
    score = Math.max(score, 0.4);
  }

  if (!hasExif && jpegLike) {
    reasons.push('данные о съёмке (EXIF) отсутствуют — они могли быть удалены при обработке');
    score = Math.max(score, 0.35);
  } else if (hasExif && !hasDate && hasCamera) {
    reasons.push('в EXIF нет даты съёмки');
    score = Math.max(score, 0.25);
  }

  let confidence = 0.35;
  if (software) confidence = 0.85;
  else if (hasExif) confidence = 0.6;

  return {
    id: 'metadata',
    score,
    confidence,
    note:
      reasons.length > 0
        ? `метаданные: ${reasons.join('; ')}`
        : 'метаданные не указывают на редактирование',
  };
};

const PRODUCERS: Record<AuthenticitySignalId, (ctx: CheckRunContext) => AuthenticitySignal> = {
  compression_history: compressionHistorySignal,
  metadata: metadataSignal,
  resampling: resamplingSignal,
  face_texture: faceTextureSignal,
  skin_smoothing: skinSmoothingSignal,
  boundary: boundarySignal,
  noise_mismatch: noiseMismatchSignal,
  clone: cloneSignal,
};

export interface AuthenticityOptions {
  /** Which signals to run; defaults to the full ensemble. */
  signalIds?: readonly AuthenticitySignalId[];
  /** Overrides for the default weights (the check exposes them in `details`). */
  weights?: Partial<Record<AuthenticitySignalId, number>>;
  /** Signals at/above this score are listed as findings. */
  findingFloor?: number;
  /** Hard cap on the risk when only `metadata` is elevated. */
  metadataOnlyRiskCap?: number;
}

/** The single combined risk used by every authenticity check. */
export const assessAuthenticity = (
  ctx: CheckRunContext,
  options: AuthenticityOptions = {},
): AuthenticityAssessment => {
  const ids = options.signalIds ?? SIGNAL_ORDER;
  const weightOf = (id: AuthenticitySignalId): number =>
    options.weights?.[id] ?? AUTHENTICITY_WEIGHTS[id];

  const signals: AuthenticitySignal[] = [];
  const unavailable: string[] = [];

  for (const id of ids) {
    const produced = PRODUCERS[id](ctx);
    if (!Number.isFinite(produced.score) || !Number.isFinite(produced.confidence)) continue;
    if (produced.confidence < UNAVAILABLE_CONFIDENCE) {
      unavailable.push(`${id} — ${produced.note}`);
      continue;
    }
    signals.push({
      id,
      score: clamp(produced.score, 0, 1),
      confidence: clamp(produced.confidence, 0, 1),
      note: produced.note,
    });
  }

  /**
   * Signal I of the brief. There is no offline AI classifier in the core and
   * there will not be one: guessing here would be exactly the dishonesty the
   * whole design avoids. The limitation is reported, never hidden.
   */
  unavailable.push(
    'ai_classifier — офлайн-классификатор ИИ-генерации недоступен; оценка основана только на косвенных признаках, а не на модели.',
  );

  const totalRequested = ids.reduce((sum, id) => sum + weightOf(id), 0);
  const totalAvailable = signals.reduce((sum, signal) => sum + weightOf(signal.id), 0);

  const weightedRisk =
    totalAvailable > 0
      ? signals.reduce((sum, signal) => sum + weightOf(signal.id) * signal.score, 0) /
        totalAvailable
      : 0;
  const weightedConfidence =
    totalAvailable > 0
      ? signals.reduce((sum, signal) => sum + weightOf(signal.id) * signal.confidence, 0) /
        totalAvailable
      : 0;
  const coverage = totalRequested > 0 ? totalAvailable / totalRequested : 0;

  const metadataOnlyRiskCap =
    options.metadataOnlyRiskCap ?? numberParam(ctx.rule.params, 'metadataOnlyRiskCap', 0.25);
  const supportFloor = numberParam(ctx.rule.params, 'metadataSupportFloor', 0.25);
  const supported = signals.some(
    (signal) => signal.id !== 'metadata' && signal.score >= supportFloor,
  );

  const risk = supported ? weightedRisk : Math.min(weightedRisk, metadataOnlyRiskCap);
  const confidence = clamp(weightedConfidence * (0.4 + 0.6 * coverage), 0, 1);

  const findingFloor = options.findingFloor ?? numberParam(ctx.rule.params, 'findingFloor', 0.3);
  const findings = signals
    .filter((signal) => signal.score >= findingFloor)
    .sort(
      (a, b) => weightOf(b.id) * b.score - weightOf(a.id) * a.score,
    )
    .map((signal) => signal.note);

  return {
    risk: clamp(risk, 0, 1),
    confidence,
    signals,
    findings,
    unavailable,
  };
};

/** The full ensemble — used by the `ai_manipulation` check. */
export const assessAiManipulation = (ctx: CheckRunContext): AuthenticityAssessment =>
  assessAuthenticity(ctx, { signalIds: SIGNAL_ORDER });

// ---------------------------------------------------------------------------
// Shared verdict helpers (used by all three checks)
// ---------------------------------------------------------------------------

export const DISCLAIMER =
  'Автоматическая проверка может ошибаться. Обнаружение признаков цифровой обработки является предупреждением, а не доказательством способа создания фотографии.';

export const confidenceWord = (confidence: number): 'низкая' | 'средняя' | 'высокая' =>
  confidence >= 0.7 ? 'высокая' : confidence >= 0.4 ? 'средняя' : 'низкая';

export const formatFindings = (findings: readonly string[]): string =>
  findings.map((finding) => `• ${finding}`).join(' ');

export interface RiskVerdict {
  status: MeasurementStatus;
  reason: 'ok' | 'below_threshold' | 'low_confidence' | 'above_fail_threshold';
}

/**
 * Turns risk + confidence into a status.
 *
 * The maximum status is `warning` unless the profile explicitly opts into a
 * failure via `failRisk`, and a failure additionally requires the confidence to
 * clear `failConfidence`. Low confidence always degrades to `uncertain`, never
 * to a pass or a fail.
 */
export const judgeRisk = (input: {
  risk: number;
  confidence: number;
  warnRisk: number;
  uncertainBelowConfidence: number;
  failRisk?: number;
  failConfidence: number;
}): RiskVerdict => {
  const { risk, confidence, warnRisk, uncertainBelowConfidence } = input;
  if (!Number.isFinite(risk) || !Number.isFinite(confidence)) {
    return { status: 'uncertain', reason: 'low_confidence' };
  }
  if (confidence < uncertainBelowConfidence) {
    return { status: 'uncertain', reason: 'low_confidence' };
  }
  if (
    input.failRisk !== undefined &&
    risk >= input.failRisk &&
    confidence >= input.failConfidence
  ) {
    return { status: 'fail', reason: 'above_fail_threshold' };
  }
  if (risk >= warnRisk) {
    return { status: 'warning', reason: 'below_threshold' };
  }
  return { status: 'pass', reason: 'ok' };
};
